/**
 * Reference frames and coordinate conversions.
 *
 * Three frames are used:
 *
 * - **ECI** (Earth-Centred Inertial) — where all integration happens. Non-rotating,
 *   z along the rotation axis, x toward the vernal equinox at the epoch.
 * - **ECEF** (Earth-Centred Earth-Fixed) — rotates with the planet. Launch sites,
 *   ground tracks and the atmosphere live here.
 * - **Geodetic** — latitude, longitude and altitude above the WGS84 ellipsoid.
 *
 * ECI and ECEF differ by a single rotation about z through the Greenwich mean
 * sidereal angle. Precession, nutation and polar motion are neglected (Tier B);
 * over a mission of days that is a sub-kilometre effect, and it keeps the frame
 * chain cheap enough to run inside the integrator.
 */

import { EARTH, type CelestialBodyParams } from './constants.js';
import { clampUnit } from './units.js';
import { cross, dot, norm, normalize, rotateX, rotateZ, sub, type ReadonlyVec3, type Vec3 } from './vec3.js';

export interface Geodetic {
  /** Geodetic latitude, radians, positive north. */
  latitude: number;
  /** Longitude, radians, positive east. */
  longitude: number;
  /** Height above the reference ellipsoid, metres. */
  altitude: number;
}

export interface AzElRange {
  /** Azimuth from true north, radians, increasing eastward. */
  azimuth: number;
  /** Elevation above the local horizon, radians. */
  elevation: number;
  /** Slant range, metres. */
  range: number;
}

/** Earth's angular velocity vector in either ECI or ECEF (it lies along z in both). */
export const earthRotationVector = (body: CelestialBodyParams = EARTH): Vec3 => [
  0,
  0,
  body.rotationRate,
];

/** Rotate an inertial position into the body-fixed frame. */
export const eciToEcef = (rEci: ReadonlyVec3, gmstRad: number): Vec3 => rotateZ(rEci, -gmstRad);

/** Rotate a body-fixed position into the inertial frame. */
export const ecefToEci = (rEcef: ReadonlyVec3, gmstRad: number): Vec3 => rotateZ(rEcef, gmstRad);

/**
 * Inertial velocity of a point that is fixed to the rotating body.
 * This is what a launch site contributes at lift-off — 465 m/s at the equator,
 * and the reason equatorial sites are valuable.
 */
export const coRotatingVelocity = (
  rEci: ReadonlyVec3,
  body: CelestialBodyParams = EARTH,
): Vec3 => cross(earthRotationVector(body), rEci);

/**
 * Velocity relative to the co-rotating atmosphere, expressed in ECI axes.
 * Drag must use this, never the inertial velocity.
 */
export const atmosphereRelativeVelocity = (
  rEci: ReadonlyVec3,
  vEci: ReadonlyVec3,
  body: CelestialBodyParams = EARTH,
): Vec3 => sub(vEci, coRotatingVelocity(rEci, body));

/** Convert an inertial velocity to the body-fixed frame. */
export function eciToEcefVelocity(
  rEci: ReadonlyVec3,
  vEci: ReadonlyVec3,
  gmstRad: number,
  body: CelestialBodyParams = EARTH,
): Vec3 {
  return eciToEcef(atmosphereRelativeVelocity(rEci, vEci, body), gmstRad);
}

/** Convert a body-fixed velocity to the inertial frame. */
export function ecefToEciVelocity(
  rEcef: ReadonlyVec3,
  vEcef: ReadonlyVec3,
  gmstRad: number,
  body: CelestialBodyParams = EARTH,
): Vec3 {
  const rEci = ecefToEci(rEcef, gmstRad);
  const vInertialPart = ecefToEci(vEcef, gmstRad);
  const omegaCrossR = coRotatingVelocity(rEci, body);
  return [
    vInertialPart[0] + omegaCrossR[0],
    vInertialPart[1] + omegaCrossR[1],
    vInertialPart[2] + omegaCrossR[2],
  ];
}

/** Geodetic coordinates to an ECEF position on the WGS84 ellipsoid. */
export function geodeticToEcef(g: Geodetic, body: CelestialBodyParams = EARTH): Vec3 {
  const a = body.equatorialRadius;
  const f = body.flattening;
  const e2 = f * (2 - f);
  const sinLat = Math.sin(g.latitude);
  const cosLat = Math.cos(g.latitude);
  // Radius of curvature in the prime vertical.
  const N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
  const xy = (N + g.altitude) * cosLat;
  return [
    xy * Math.cos(g.longitude),
    xy * Math.sin(g.longitude),
    (N * (1 - e2) + g.altitude) * sinLat,
  ];
}

/**
 * ECEF position to geodetic coordinates on the WGS84 ellipsoid.
 *
 * Fixed-point iteration on latitude. Converges to well under a millimetre in
 * four passes for any altitude a launch vehicle or satellite reaches, and unlike
 * the closed-form Ferrari solution it stays numerically clean near the poles.
 */
export function ecefToGeodetic(rEcef: ReadonlyVec3, body: CelestialBodyParams = EARTH): Geodetic {
  const a = body.equatorialRadius;
  const f = body.flattening;
  const e2 = f * (2 - f);
  const [x, y, z] = rEcef;
  const p = Math.hypot(x, y);
  const longitude = Math.atan2(y, x);

  // On the rotation axis the longitude is undefined; report 0 and solve altitude directly.
  if (p < 1e-9) {
    const b = body.polarRadius;
    return {
      latitude: z >= 0 ? Math.PI / 2 : -Math.PI / 2,
      longitude: 0,
      altitude: Math.abs(z) - b,
    };
  }

  // Refine the latitude; the height falls out of the converged value afterwards.
  let latitude = Math.atan2(z, p * (1 - e2));
  for (let i = 0; i < 6; i++) {
    const sinLat = Math.sin(latitude);
    const N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
    const h = p / Math.cos(latitude) - N;
    const next = Math.atan2(z, p * (1 - (e2 * N) / (N + h)));
    const converged = Math.abs(next - latitude) < 1e-13;
    latitude = next;
    if (converged) break;
  }

  const sinLat = Math.sin(latitude);
  const N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
  const altitude = p / Math.cos(latitude) - N;

  return { latitude, longitude, altitude };
}

/** Geodetic coordinates of an inertial position at a given sidereal angle. */
export const eciToGeodetic = (
  rEci: ReadonlyVec3,
  gmstRad: number,
  body: CelestialBodyParams = EARTH,
): Geodetic => ecefToGeodetic(eciToEcef(rEci, gmstRad), body);

/**
 * Geometric altitude above the ellipsoid. Cheaper than a full geodetic solve and
 * used on the integrator's hot path, where it is called for every force evaluation.
 *
 * Uses the local ellipsoid radius at the geocentric latitude rather than measuring
 * along the ellipsoid normal. The two agree exactly on the surface and diverge
 * with altitude. Measured worst case: 0.56 m up to 100 km, 4.9 m at the 1000 km
 * ceiling of the atmosphere model, 35 m at lunar distance. Negligible against the
 * atmosphere model's own uncertainty, which is the only thing this feeds.
 *
 * Use `ecefToGeodetic` when the altitude itself is the answer being reported.
 */
export function altitudeAbove(rEci: ReadonlyVec3, body: CelestialBodyParams = EARTH): number {
  const r = norm(rEci);
  if (r === 0) return -body.equatorialRadius;
  const sinLatSq = (rEci[2] / r) * (rEci[2] / r);
  const a = body.equatorialRadius;
  const b = body.polarRadius;
  // Radius of the ellipsoid at this geocentric latitude.
  const localRadius = (a * b) / Math.sqrt(b * b + (a * a - b * b) * sinLatSq);
  return r - localRadius;
}

/** Orthonormal East–North–Up basis at a geodetic location, expressed in ECEF. */
export function enuBasis(g: Geodetic): { east: Vec3; north: Vec3; up: Vec3 } {
  const sinLat = Math.sin(g.latitude);
  const cosLat = Math.cos(g.latitude);
  const sinLon = Math.sin(g.longitude);
  const cosLon = Math.cos(g.longitude);
  return {
    east: [-sinLon, cosLon, 0],
    north: [-sinLat * cosLon, -sinLat * sinLon, cosLat],
    up: [cosLat * cosLon, cosLat * sinLon, sinLat],
  };
}

/** Project an ECEF vector into the local East–North–Up frame at `g`. */
export function ecefToEnu(vecEcef: ReadonlyVec3, g: Geodetic): Vec3 {
  const { east, north, up } = enuBasis(g);
  return [dot(vecEcef, east), dot(vecEcef, north), dot(vecEcef, up)];
}

/** Lift a local East–North–Up vector back into ECEF. */
export function enuToEcef(vecEnu: ReadonlyVec3, g: Geodetic): Vec3 {
  const { east, north, up } = enuBasis(g);
  return [
    vecEnu[0] * east[0] + vecEnu[1] * north[0] + vecEnu[2] * up[0],
    vecEnu[0] * east[1] + vecEnu[1] * north[1] + vecEnu[2] * up[1],
    vecEnu[0] * east[2] + vecEnu[1] * north[2] + vecEnu[2] * up[2],
  ];
}

/**
 * Look angles from a ground station to a target. This is what drives the
 * visibility windows and coverage statistics in the analytics panel.
 */
export function lookAngles(
  station: Geodetic,
  targetEcef: ReadonlyVec3,
  body: CelestialBodyParams = EARTH,
): AzElRange {
  const stationEcef = geodeticToEcef(station, body);
  const enu = ecefToEnu(sub(targetEcef, stationEcef), station);
  const [e, n, u] = enu;
  const range = Math.hypot(e, n, u);
  const azimuth = Math.atan2(e, n);
  return {
    azimuth: azimuth < 0 ? azimuth + 2 * Math.PI : azimuth,
    elevation: range === 0 ? 0 : Math.asin(clampUnit(u / range)),
    range,
  };
}

/**
 * Inertial state of a point fixed to the rotating body — the initial condition
 * for every launch. The velocity is purely the Earth-rotation contribution.
 */
export function siteStateEci(
  site: Geodetic,
  tEpochSeconds: number,
  gmstFn: (t: number) => number,
  body: CelestialBodyParams = EARTH,
): { position: Vec3; velocity: Vec3 } {
  const theta = gmstFn(tEpochSeconds);
  const position = ecefToEci(geodeticToEcef(site, body), theta);
  return { position, velocity: coRotatingVelocity(position, body) };
}

/**
 * Great-circle distance between two geodetic points along the mean sphere, m.
 * Uses the haversine form, which stays accurate for the very small separations
 * that appear when scoring a landing prediction against its target.
 */
export function greatCircleDistance(
  a: Geodetic,
  b: Geodetic,
  radius = EARTH.equatorialRadius,
): number {
  const dLat = b.latitude - a.latitude;
  const dLon = b.longitude - a.longitude;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.latitude) * Math.cos(b.latitude) * Math.sin(dLon / 2) ** 2;
  return 2 * radius * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial great-circle bearing from `a` to `b`, radians from true north. */
export function initialBearing(a: Geodetic, b: Geodetic): number {
  const dLon = b.longitude - a.longitude;
  const y = Math.sin(dLon) * Math.cos(b.latitude);
  const x =
    Math.cos(a.latitude) * Math.sin(b.latitude) -
    Math.sin(a.latitude) * Math.cos(b.latitude) * Math.cos(dLon);
  const brg = Math.atan2(y, x);
  return brg < 0 ? brg + 2 * Math.PI : brg;
}

/** Rotate an equatorial (ECI) vector into ecliptic coordinates. */
export const equatorialToEcliptic = (v: ReadonlyVec3, obliquity = EARTH.obliquity): Vec3 =>
  rotateX(v, -obliquity);

/** Rotate an ecliptic vector into equatorial (ECI) coordinates. */
export const eclipticToEquatorial = (v: ReadonlyVec3, obliquity = EARTH.obliquity): Vec3 =>
  rotateX(v, obliquity);

/**
 * Local vertical / local horizontal basis for a state vector: radial, along-track
 * (in-plane, perpendicular to radial) and cross-track. Manoeuvres are specified
 * in this frame because "prograde" and "radial" are how operators think.
 */
export function lvlhBasis(
  r: ReadonlyVec3,
  v: ReadonlyVec3,
): { radial: Vec3; prograde: Vec3; normal: Vec3 } {
  const radial = normalize(r);
  const normal = normalize(cross(r, v));
  const prograde = normalize(cross(normal, radial));
  return { radial, prograde, normal };
}

/**
 * Flight path angle: the angle between the velocity vector and the local
 * horizontal, radians. Zero at apoapsis and periapsis, positive while climbing.
 */
export function flightPathAngle(r: ReadonlyVec3, v: ReadonlyVec3): number {
  const speed = norm(v);
  if (speed === 0) return 0;
  return Math.asin(clampUnit(dot(normalize(r), v) / speed));
}
